<template>
  <div :id="$attrs.id" class="chat-message-outer" @click="resetLanguageMenuWidth">
    <div
      class="chat-message"
      :class="[
        {
          'chat-message--customer': isCustomer,
          'chat-message--is-deleted': isDeleted,
          'markdown-container': isConcierge
        }
      ]"
    >
      <div v-if="isConcierge && showAvatar" class="chat-message__header">
        {{ operatorName }}
      </div>
      <div class="chat-message__inline-block">
        <div ref="msgContainer" class="chat-message__container">
          <template v-if="isConcierge && showAvatar">
            <div
              v-if="showAvatar"
              class="chat-message__avatar"
              :style="{ ...appStore.conciergeIconStyle }"
            ></div>
            <div v-else class="chat-message__avatar"></div>
          </template>

          <div
            v-if="showPopover"
            ref="popover"
            class="chat-message__popover"
            :class="[popoverPlacement]"
            :style="{ right: `${popoverRight}px` }"
          >
            <icon-delete
              style="width: 24px; height: 24px; fill: #797979; cursor: pointer"
              @click="handleDelete"
            />
          </div>

          <div class="chat-message__main" :style="chatMessageStyles">
            <div
              v-if="!hideMessage"
              class="chat-message__content"
              :class="[{ 'no-select': isMobile }]"
              @touchstart="handleTouchStart"
              @touchend="handleTouchEnd"
            >
              <slot name="message">
                <p v-if="isDeleted">
                  {{ chat.user ? $t('chat_body.deleted_message') : $t('chat_body.op_deleted_msg') }}
                </p>
                <p v-else-if="isCustomer">{{ chat.message }}</p>
                <p v-else :class="[{ markdown: isConcierge }]" v-html="formattedChatMessage"></p>
              </slot>
            </div>

            <slot></slot>
          </div>

          <div v-if="isCustomer && !isDeleted" class="chat-message__operations">
            <icon-delete class="icon" @click="$emit('delete', chat)" />
          </div>
        </div>

        <chat-message-footer :chat="chat" :operator-name="operatorName"></chat-message-footer>
        <chat-evaluation-widget v-if="isUserEvaluationType" :chat="chat"></chat-evaluation-widget>
      </div>
    </div>
  </div>
</template>

<script>
import ChatMessageFooter from 'Chats/ChatMessageFooter'
import ChatEvaluationWidget from 'Chats/ChatEvaluationWidget'
import { formatMarkdownMessage, isMobile } from 'Utilities'
import { defaultOpName } from 'javascript/Constants'
import IconDelete from 'assets/icons/delete.svg?inline'
import { EventBus } from 'ChatWidget/EventBus'
import { useAppStore } from 'Store/app'
import { useSettingsStore } from 'Store/settings'
import { useColorsStore } from 'Store/colors'

export default {
  name: 'ChatMessage',
  components: { ChatMessageFooter, IconDelete, ChatEvaluationWidget },
  inheritAttrs: false,

  setup() {
    const appStore = useAppStore()
    const settingsStore = useSettingsStore()
    const colorsStore = useColorsStore()
    const resetLanguageMenuWidth = (event) => {
      event.stopPropagation()
      if (settingsStore.isClickLanguageMenu) {
        settingsStore.toggleLanguageMenu(false)
      }
    }
    return { appStore, settingsStore, colorsStore, resetLanguageMenuWidth }
  },

  props: {
    chat: { type: Object, required: true },
    prevChat: { type: Object, default: null },
    hideMessage: Boolean,
    hideAvatar: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      isMobile: isMobile(),
      showPopover: false,
      popoverRight: 0,
      popoverPlacement: 'top',
      popoverTimeoutId: null
    }
  },

  computed: {
    chatHeader() {
      return this.settingsStore.getSettings[this.$i18n.locale].chat_header
    },

    operatorName() {
      return this.chatHeader.operator_name ? this.chatHeader.operator_name : defaultOpName
    },

    isUserEvaluationType() {
      return this.chat.message_rich && this.chat.message_rich.type
        ? this.chat.message_rich.type === 'evaluation'
        : false
    },

    formattedChatMessage() {
      const message =
        this.chat.message_rich && this.chat.message_rich.type
          ? this.chat.message_rich.message
          : this.chat.message

      return formatMarkdownMessage(message)
    },

    isDeleted() {
      return !!this.chat.deleted_at
    },

    showAvatar() {
      if (this.hideAvatar) return false
      if (!this.isConcierge) return false
      if (!this.prevChat || this.prevChat.user) return true

      const publishedAt = new Date(this.chat.published_at)
      const prevPublishedAt = new Date(this.prevChat.published_at)
      return publishedAt.getDate() !== prevPublishedAt.getDate()
    },

    isCustomer() {
      return !!this.chat.user
    },

    isConcierge() {
      return !!this.chat.concierge
    },

    chatMessageStyles() {
      if (this.settingsStore.isClickLanguageMenu) {
        return {
          width: '280px'
        }
      }
      return {}
    }
  },

  mounted() {
    this.$nextTick(() => {
      EventBus.$emit('scroll-to-latest-message')
    })
  },

  methods: {
    handleDelete() {
      this.$emit('delete', this.chat)
      this.showPopover = false
      this.$root.$el.removeEventListener('click', this.outsideClickListener)
    },

    outsideClickListener(event) {
      const element = this.$refs['popover']
      if (!element.contains(event.target)) {
        this.showPopover = false
        this.$root.$el.removeEventListener('click', this.outsideClickListener)
      }
    },

    handleTouchStart(event) {
      if (!this.isCustomer || this.isDeleted) return

      this.popoverTimeoutId = setTimeout(() => {
        const rect = this.$el.getBoundingClientRect()
        this.popoverPlacement = rect.top > 100 ? 'top' : 'bottom'
        this.popoverRight = rect.width - event.touches[0].pageX
        this.showPopover = true

        this.$nextTick(() => {
          this.$root.$el.addEventListener('click', this.outsideClickListener)
        })
      }, 500)
    },

    handleTouchEnd() {
      clearTimeout(this.popoverTimeoutId)
    }
  }
}
</script>
